/**
 * File kinds — the client's mirror of `@fias/db-types` vault-file-kinds.ts.
 *
 * The coarse "what sort of file is this" behind the file dialogs' type chips.
 * The server filters by the same table (`fileKindSqlCase`), so a chip and the
 * rows it returns cannot disagree. The client does not depend on
 * `@fias/db-types`, so this is a copy — kept in step by
 * `tests/architecture/vault-area-policy-client-parity.test.ts`.
 *
 * Order matters: the first matching rule wins (a `text/csv` is a spreadsheet,
 * not generic text).
 */
// Imports nothing: `tests/architecture/` loads this file outside the client's
// module graph (no `@/` alias there).

export const VAULT_FILE_KINDS = [
  'image',
  'pdf',
  'document',
  'spreadsheet',
  'presentation',
  'audio',
  'video',
  'archive',
  'code',
  'other',
] as const;

export type VaultFileKind = (typeof VAULT_FILE_KINDS)[number];

export const VAULT_FILE_KIND_LABELS: Record<VaultFileKind, string> = {
  image: 'Images',
  pdf: 'PDFs',
  document: 'Documents',
  spreadsheet: 'Spreadsheets',
  presentation: 'Presentations',
  audio: 'Audio',
  video: 'Video',
  archive: 'Archives',
  code: 'Code',
  other: 'Other',
};

interface KindRule {
  kind: Exclude<VaultFileKind, 'other'>;
  exact?: readonly string[];
  prefixes?: readonly string[];
}

const KIND_RULES: readonly KindRule[] = [
  { kind: 'pdf', exact: ['application/pdf'] },
  {
    kind: 'spreadsheet',
    exact: [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet',
    ],
  },
  {
    kind: 'presentation',
    exact: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.oasis.opendocument.presentation',
    ],
  },
  {
    kind: 'document',
    exact: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
      'application/rtf',
      'text/plain',
      'text/markdown',
      'text/html',
    ],
  },
  {
    kind: 'code',
    exact: [
      'application/json',
      'application/javascript',
      'application/typescript',
      'application/xml',
      'text/xml',
      'text/css',
      'text/javascript',
    ],
  },
  {
    kind: 'archive',
    exact: [
      'application/zip',
      'application/x-zip-compressed',
      'application/gzip',
      'application/x-tar',
      'application/x-7z-compressed',
    ],
  },
  { kind: 'image', prefixes: ['image/'] },
  { kind: 'audio', prefixes: ['audio/'] },
  { kind: 'video', prefixes: ['video/'] },
];

/** The kind of a MIME type (`null` / unknown → `other`). */
export function mimeToFileKind(mimeType: string | null | undefined): VaultFileKind {
  const mime = (mimeType ?? '').toLowerCase().split(';')[0].trim();
  if (!mime) return 'other';
  for (const rule of KIND_RULES) {
    if (rule.exact?.includes(mime)) return rule.kind;
    if (rule.prefixes?.some((p) => mime.startsWith(p))) return rule.kind;
  }
  return 'other';
}
